import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  FileText,
  Search,
  Download,
  Upload,
  ChevronDown,
  ChevronRight,
  Building2,
  Calendar,
  Filter,
  AlertCircle,
  Loader2,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

export const RecepcionesPendientes = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  
  // Filtros
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [proveedorFilter, setProveedorFilter] = useState('');
  
  // Selección para exportar
  const [selectedItems, setSelectedItems] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  
  // Detalle expandido
  const [expandedItems, setExpandedItems] = useState({});
  
  // Modal de detalle
  const [detailModal, setDetailModal] = useState({ open: false, item: null });
  
  // Modal de confirmación de exportación
  const [exportModal, setExportModal] = useState({ open: false });
  const [exportResults, setExportResults] = useState(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setSelectedItems({});
    setSelectAll(false);
    
    try {
      const params = new URLSearchParams();
      if (yearFilter && yearFilter !== 'todos') {
        params.append('year', yearFilter);
      }
      if (proveedorFilter) {
        params.append('proveedor', proveedorFilter);
      }
      
      const res = await api.get(`/recepciones-pendientes?${params.toString()}`);
      setData(res.data);
    } catch (err) {

      setError(err.response?.data?.detail || 'Error al cargar recepciones pendientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    loadData();
  };

  const toggleExpand = (rnum) => {
    setExpandedItems(prev => ({
      ...prev,
      [rnum]: !prev[rnum]
    }));
  };

  const toggleSelect = (rnum) => {
    setSelectedItems(prev => ({
      ...prev,
      [rnum]: !prev[rnum]
    }));
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (data?.recepciones) {
      const newSelected = {};
      data.recepciones.forEach(r => {
        newSelected[r.rnum] = checked;
      });
      setSelectedItems(newSelected);
    }
  };

  const getSelectedCount = () => {
    return Object.values(selectedItems).filter(Boolean).length;
  };

  const getSelectedTotal = () => {
    if (!data?.recepciones) return 0;
    return data.recepciones
      .filter(r => selectedItems[r.rnum])
      .reduce((sum, r) => sum + (r.monto || 0), 0);
  };

  const formatMoney = (value) => {
    if (!value && value !== 0) return '-';
    return '$' + Math.round(value).toLocaleString('es-CL');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('/');
    if (parts.length >= 3) {
      return `${parts[1]}/${parts[0]}/${parts[2]}`;
    }
    return dateStr;
  };

  const handleExport = async () => {
    const selectedRnums = Object.entries(selectedItems)
      .filter(([_, selected]) => selected)
      .map(([rnum, _]) => rnum);
    
    if (selectedRnums.length === 0) {
      toast({ title: 'Seleccione al menos una recepción', variant: 'destructive' });
      return;
    }
    
    setExporting(true);
    setExportResults(null);
    
    try {
      const res = await api.post('/recepciones-pendientes/exportar-alegra', selectedRnums);
      setExportResults(res.data);
      
      if (res.data.exportados > 0) {
        toast({ 
          title: `${res.data.exportados} factura(s) exportada(s) a Alegra`,
          variant: 'default'
        });
        // Recargar datos
        loadData();
      }
      
      if (res.data.errores > 0) {
        toast({ 
          title: `${res.data.errores} error(es) durante la exportación`,
          variant: 'destructive'
        });
      }
    } catch (err) {

      toast({ 
        title: err.response?.data?.detail || 'Error al exportar a Alegra',
        variant: 'destructive'
      });
    } finally {
      setExporting(false);
      setExportModal({ open: false });
    }
  };

  const exportToCSV = () => {
    if (!data?.recepciones) return;
    
    const headers = ['Recepción', 'Fecha', 'Factura#', 'Monto', 'RUT Proveedor', 'OT', 'Condición'];
    const rows = data.recepciones.map(r => [
      r.rnum,
      formatDate(r.fecha),
      r.numero_factura,
      r.monto,
      r.rut_proveedor,
      r.centro_costo,
      r.condicion
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(c => `"${c}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `recepciones_pendientes_${yearFilter || 'todos'}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Recepciones Pendientes</h1>
          <p className="text-sm text-muted-foreground">
            Facturas recepcionadas en Access que no están en Alegra
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV} disabled={!data?.recepciones?.length}>
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button 
            onClick={() => setExportModal({ open: true })} 
            disabled={getSelectedCount() === 0 || exporting}
            className="bg-green-600 hover:bg-green-700"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Exportar a Alegra ({getSelectedCount()})
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold">Filtros</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Año</label>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger data-testid="year-filter">
                <SelectValue placeholder="Seleccionar año" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los años</SelectItem>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">RUT Proveedor</label>
            <Input
              placeholder="Buscar por RUT..."
              value={proveedorFilter}
              onChange={(e) => setProveedorFilter(e.target.value)}
              data-testid="proveedor-filter"
            />
          </div>
          
          <div className="flex items-end">
            <Button onClick={handleSearch} disabled={loading} className="w-full">
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Buscar
            </Button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Totales */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              <FileText className="w-5 h-5" />
              <span className="font-medium">Recepciones Pendientes</span>
            </div>
            <p className="text-2xl font-bold">{data.totales?.cantidad || 0}</p>
          </div>
          
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-green-600 mb-2">
              <Building2 className="w-5 h-5" />
              <span className="font-medium">Monto Total</span>
            </div>
            <p className="text-2xl font-bold">{formatMoney(data.totales?.monto)}</p>
          </div>
          
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-purple-600 mb-2">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Seleccionadas</span>
            </div>
            <p className="text-2xl font-bold">{getSelectedCount()}</p>
            <p className="text-xs text-muted-foreground">{formatMoney(getSelectedTotal())}</p>
          </div>
          
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-orange-600 mb-2">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">En Alegra</span>
            </div>
            <p className="text-2xl font-bold">{data.facturas_en_alegra || 0}</p>
            <p className="text-xs text-muted-foreground">Facturas sincronizadas</p>
          </div>
        </div>
      )}

      {/* Tabla Principal */}
      {loading ? (
        <div className="bg-white rounded-lg border p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2">Cargando recepciones...</span>
        </div>
      ) : data ? (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-10">
                    <Checkbox 
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all"
                    />
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Recepción</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Factura#</TableHead>
                  <TableHead>RUT Proveedor</TableHead>
                  <TableHead>OT</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recepciones?.map((r) => (
                  <>
                    <TableRow key={r.rnum} className={selectedItems[r.rnum] ? 'bg-blue-50' : ''}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedItems[r.rnum] || false}
                          onCheckedChange={() => toggleSelect(r.rnum)}
                        />
                      </TableCell>
                      <TableCell>
                        {r.detalles?.length > 0 && (
                          <button onClick={() => toggleExpand(r.rnum)} className="p-1">
                            {expandedItems[r.rnum] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{r.rnum}</TableCell>
                      <TableCell>{formatDate(r.fecha)}</TableCell>
                      <TableCell className="font-semibold">{r.numero_factura}</TableCell>
                      <TableCell>{r.rut_proveedor}</TableCell>
                      <TableCell>{r.centro_costo}</TableCell>
                      <TableCell className="text-right font-semibold">{formatMoney(r.monto)}</TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setDetailModal({ open: true, item: r })}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    
                    {/* Detalle expandido */}
                    {expandedItems[r.rnum] && r.detalles?.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="bg-gray-50 p-0">
                          <div className="p-4">
                            <h4 className="font-semibold mb-2">Detalle de Items ({r.detalles.length})</h4>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Item</TableHead>
                                  <TableHead>Descripción</TableHead>
                                  <TableHead>Código</TableHead>
                                  <TableHead className="text-right">Cantidad</TableHead>
                                  <TableHead className="text-right">V. Unit</TableHead>
                                  <TableHead className="text-right">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {r.detalles.map((d, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{d.item}</TableCell>
                                    <TableCell className="max-w-[200px] truncate">{d.descripcion}</TableCell>
                                    <TableCell className="font-mono text-xs">{d.codigo_material}</TableCell>
                                    <TableCell className="text-right">{d.cantidad_recibida}</TableCell>
                                    <TableCell className="text-right">{formatMoney(d.valor_unitario)}</TableCell>
                                    <TableCell className="text-right">{formatMoney(d.total)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}

      {/* Resultados de Exportación */}
      {exportResults && (
        <div className={`rounded-lg border p-4 ${exportResults.errores > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
          <h3 className="font-semibold mb-2">Resultado de Exportación</h3>
          <div className="flex gap-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span>{exportResults.exportados} exportada(s)</span>
            </div>
            {exportResults.errores > 0 && (
              <div className="flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" />
                <span>{exportResults.errores} error(es)</span>
              </div>
            )}
          </div>
          {exportResults.detalle_errores?.length > 0 && (
            <div className="mt-2 text-sm text-red-600">
              {exportResults.detalle_errores.map((e, i) => (
                <div key={i}>Recepción {e.rnum}: {e.error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Información</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Muestra facturas recepcionadas en Access (tabla recepciones) que no están en Alegra</li>
          <li>• Solo se muestran recepciones tipo "FA" (Factura)</li>
          <li>• Seleccione las facturas que desea exportar y haga clic en "Exportar a Alegra"</li>
          <li>• Haga clic en el ícono de ojo para ver el detalle completo de cada recepción</li>
          <li>• Use la flecha para expandir y ver los items de cada factura</li>
        </ul>
      </div>

      {/* Modal de Detalle */}
      <Dialog open={detailModal.open} onOpenChange={(open) => setDetailModal({ open, item: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de Recepción {detailModal.item?.rnum}</DialogTitle>
          </DialogHeader>
          
          {detailModal.item && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Número Factura:</span>
                  <span className="ml-2">{detailModal.item.numero_factura}</span>
                </div>
                <div>
                  <span className="font-medium">Fecha:</span>
                  <span className="ml-2">{formatDate(detailModal.item.fecha)}</span>
                </div>
                <div>
                  <span className="font-medium">RUT Proveedor:</span>
                  <span className="ml-2">{detailModal.item.rut_proveedor}</span>
                </div>
                <div>
                  <span className="font-medium">OT/Centro Costo:</span>
                  <span className="ml-2">{detailModal.item.centro_costo}</span>
                </div>
                <div>
                  <span className="font-medium">Monto:</span>
                  <span className="ml-2 font-bold">{formatMoney(detailModal.item.monto)}</span>
                </div>
                <div>
                  <span className="font-medium">Condición:</span>
                  <span className="ml-2">{detailModal.item.condicion || '-'}</span>
                </div>
                <div>
                  <span className="font-medium">Orden:</span>
                  <span className="ml-2">{detailModal.item.orden || '-'}</span>
                </div>
                <div>
                  <span className="font-medium">Responsable:</span>
                  <span className="ml-2">{detailModal.item.responsable || '-'}</span>
                </div>
              </div>
              
              {detailModal.item.detalles?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Items ({detailModal.item.detalles.length})</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Descripción</TableHead>
                          <TableHead>Código</TableHead>
                          <TableHead className="text-right">Cant</TableHead>
                          <TableHead className="text-right">V.Unit</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailModal.item.detalles.map((d, i) => (
                          <TableRow key={i}>
                            <TableCell>{d.descripcion}</TableCell>
                            <TableCell className="font-mono text-xs">{d.codigo_material}</TableCell>
                            <TableCell className="text-right">{d.cantidad_recibida}</TableCell>
                            <TableCell className="text-right">{formatMoney(d.valor_unitario)}</TableCell>
                            <TableCell className="text-right">{formatMoney(d.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmación de Exportación */}
      <Dialog open={exportModal.open} onOpenChange={(open) => setExportModal({ open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exportación a Alegra</DialogTitle>
            <DialogDescription>
              ¿Está seguro de exportar {getSelectedCount()} factura(s) a Alegra?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-lg font-semibold">Resumen</div>
              <div className="mt-2 space-y-1 text-sm">
                <div>Facturas seleccionadas: <span className="font-bold">{getSelectedCount()}</span></div>
                <div>Monto total: <span className="font-bold">{formatMoney(getSelectedTotal())}</span></div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportModal({ open: false })}>
              Cancelar
            </Button>
            <Button onClick={handleExport} disabled={exporting} className="bg-green-600 hover:bg-green-700">
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Exportar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecepcionesPendientes;
